import React, {useEffect, useState} from "react";
import {Box, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {useNavigate} from "react-router-dom";
import {fetchChangelog, getLatestRelease, parseChangelog} from "../../utils/releaseNotesUtil";
import {
    closeIconSx,
    latestContainerSx,
    listSx,
    releaseNoteHeadingSx,
    releaseVersionSx,
    titleRowSx,
    viewReleaseHistoryButtonSx
} from "./styles";

interface ReleaseNotesProps {
    open: boolean;
    handleClose: () => void;
}

const LatestReleaseNoteContent = ({content}: { content: string }) => {
    if (!content.trim()) return <Typography variant="body1" sx={{p: 2}}>Loading release notes…</Typography>;

    const latest = getLatestRelease(parseChangelog(content));
    localStorage.setItem("viewedReleaseVersion", latest?.version ?? "");
    if (!latest) return <Typography variant="body1" sx={{p: 2}}>Release Details Not Found</Typography>;

    return (<Box sx={latestContainerSx}>
            <DialogContent sx={titleRowSx}>
                <Typography variant="h5" sx={releaseVersionSx}>Release {latest.version}</Typography>
                <Typography variant="h5" sx={releaseVersionSx}>
                    {latest.date ? new Date(latest.date).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric"
                    }) : ""}
                </Typography>
            </DialogContent>

            <DialogContent>
                {!!latest.features.length && (<>
                        <u>New</u>
                        <ul style={{listStylePosition: "inside"}} className="reset-ul">
                            {latest.features.map((f, i) => (
                                <li key={`f-${latest.version}-${i}`} style={{marginBottom: 4}}>
                                    <Box component="span" sx={listSx}><ReactMarkdown
                                        remarkPlugins={[remarkGfm]}>{f}</ReactMarkdown></Box>
                                </li>))}
                        </ul>
                    </>)}
                {!!latest.bugFixes.length && (<>
                        <u>Bug Fixes</u>
                        <ul style={{listStylePosition: "inside"}} className="reset-ul">
                            {latest.bugFixes.map((b, i) => (
                                <li key={`b-${latest.version}-${i}`} style={{marginBottom: 4}}>
                                    <Box component="span" sx={listSx}><ReactMarkdown
                                        remarkPlugins={[remarkGfm]}>{b}</ReactMarkdown></Box>
                                </li>))}
                        </ul>
                    </>)}
            </DialogContent>
        </Box>);
};

export const ReleaseNotes: React.FC<ReleaseNotesProps> = ({open, handleClose}) => {
    const navigate = useNavigate();
    const [markdown, setMarkdown] = useState("");

    useEffect(() => {
        fetchChangelog().then(setMarkdown).catch(() => setMarkdown(""));
    }, []);

    return (<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Typography variant="h5" sx={releaseNoteHeadingSx}>Release Notes</Typography>
                <IconButton aria-label="close" onClick={handleClose} sx={closeIconSx}><CloseIcon/></IconButton>
            </DialogTitle>

            <Divider/>
            <LatestReleaseNoteContent content={markdown}/>

            <Typography sx={viewReleaseHistoryButtonSx} onClick={() => {
                handleClose();
                navigate("/releasenotes");
            }}>
                View Release History
            </Typography>
        </Dialog>);
};

export default ReleaseNotes;
